import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Briefcase, Pencil, Plus, ScrollText, Trash2 } from 'lucide-react';
import {
  createOccupation,
  updateOccupation,
  deleteOccupation,
  type Occupation,
  type OccupationInput,
} from '@/api/career.api';
import { QUERY_KEYS } from '@/api/queryKeys';
import { useConfirm } from '@/components/common/ConfirmDialog';
import { useAuthStore } from '@/store/useAuthStore';
import type { PersonDetail } from '@/api/persons.api';

// ─── Zeitraum-Anzeige ────────────────────────────────────────────────────────

/** "1950–1962", nur Von → "ab 1950", nur Bis → "bis 1962", sonst null. */
function formatPeriod(occ: Pick<Occupation, 'fromText' | 'toText'>): string | null {
  if (occ.fromText && occ.toText) return `${occ.fromText}–${occ.toText}`;
  if (occ.fromText) return `ab ${occ.fromText}`;
  if (occ.toText) return `bis ${occ.toText}`;
  return null;
}

// ─── Inline-Formular (Anlegen + Bearbeiten) ─────────────────────────────────

interface FormValues {
  title: string;
  fromText: string;
  toText: string;
  employer: string;
  place: string;
  notes: string;
}

const EMPTY_FORM: FormValues = {
  title: '',
  fromText: '',
  toText: '',
  employer: '',
  place: '',
  notes: '',
};

const INPUT_CLASS =
  'w-full rounded border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-900 ' +
  'placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400';

function OccupationForm({
  initial,
  isPending,
  onSubmit,
  onCancel,
}: {
  initial: FormValues;
  isPending: boolean;
  onSubmit: (input: OccupationInput) => void;
  onCancel: () => void;
}) {
  const [values, setValues] = useState<FormValues>(initial);

  const set = (field: keyof FormValues) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setValues((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!values.title.trim()) {
      toast.error('Titel ist erforderlich.');
      return;
    }
    // Leere Felder als null senden – so werden sie beim Bearbeiten auch geleert.
    onSubmit({
      title: values.title.trim(),
      from_text: values.fromText.trim() || null,
      to_text: values.toText.trim() || null,
      employer: values.employer.trim() || null,
      place: values.place.trim() || null,
      notes: values.notes.trim() || null,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-2.5 rounded-lg border border-blue-200 bg-blue-50/30 p-3">
      <input
        type="text"
        value={values.title}
        onChange={set('title')}
        placeholder="Titel / Beruf *"
        aria-label="Titel / Beruf"
        className={INPUT_CLASS}
        autoFocus
      />
      <div className="grid grid-cols-2 gap-2">
        <input
          type="text"
          value={values.fromText}
          onChange={set('fromText')}
          placeholder="Von (z. B. 1952)"
          aria-label="Von (Jahr)"
          className={INPUT_CLASS}
        />
        <input
          type="text"
          value={values.toText}
          onChange={set('toText')}
          placeholder="Bis (z. B. 1961)"
          aria-label="Bis (Jahr)"
          className={INPUT_CLASS}
        />
      </div>
      <input
        type="text"
        value={values.employer}
        onChange={set('employer')}
        placeholder="Arbeitgeber"
        aria-label="Arbeitgeber"
        className={INPUT_CLASS}
      />
      <input
        type="text"
        value={values.place}
        onChange={set('place')}
        placeholder="Ort"
        aria-label="Ort"
        className={INPUT_CLASS}
      />
      <textarea
        value={values.notes}
        onChange={set('notes')}
        placeholder="Notiz"
        aria-label="Notiz"
        rows={2}
        className={INPUT_CLASS}
      />
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={isPending}
          className="rounded bg-blue-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm
                     hover:bg-blue-700 disabled:opacity-50"
        >
          {isPending ? 'Speichern...' : 'Speichern'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100"
        >
          Abbrechen
        </button>
      </div>
    </form>
  );
}

// ─── CareerSection ──────────────────────────────────────────────────────────
//
// Sektion "Laufbahn" im Übersichts-Tab: zeigt die Berufsstationen einer
// Person als kompakte Zeitleiste. Mit persons.manage lassen sich Stationen
// anlegen, bearbeiten und entfernen; mit archive.view führt ein Button zum
// Lebenslauf im Archiv (Deep-Link /archive?bio=<id>).

interface CareerSectionProps {
  person: PersonDetail;
}

export function CareerSection({ person }: CareerSectionProps) {
  const canManage = useAuthStore((s) => s.hasPermission('persons.manage'));
  const canViewArchive = useAuthStore((s) => s.hasPermission('archive.view'));
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  // 'new' = Formular für neue Station, Occupation = diese Station bearbeiten.
  const [editing, setEditing] = useState<'new' | Occupation | null>(null);

  const occupations = person.occupations ?? [];

  const saveOccupation = useMutation({
    mutationFn: ({ occupationId, input }: { occupationId: string | null; input: OccupationInput }) =>
      occupationId
        ? updateOccupation(person.id, occupationId, input)
        : createOccupation(person.id, input),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.persons.detail(person.id) });
      // Chronik kann parallel sichtbar sein (Panel bleibt auf /archive offen)
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.career.chronicle(person.id) });
      toast.success(vars.occupationId ? 'Station aktualisiert.' : 'Station hinzugefügt.');
      setEditing(null);
    },
    onError: () => {
      toast.error('Station konnte nicht gespeichert werden.');
    },
  });

  const removeOccupation = useMutation({
    mutationFn: (occupationId: string) => deleteOccupation(person.id, occupationId),
    onSuccess: (_data, occupationId) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.persons.detail(person.id) });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.career.chronicle(person.id) });
      // Offenes Bearbeiten-Formular der soeben gelöschten Station schließen –
      // sonst bliebe eine Formular-Leiche zurück, deren Speichern in 404 läuft.
      setEditing((prev) => (prev !== null && prev !== 'new' && prev.id === occupationId ? null : prev));
      toast.success('Station entfernt.');
    },
    onError: () => {
      toast.error('Station konnte nicht entfernt werden.');
    },
  });

  const handleDelete = useCallback(
    async (occ: Occupation) => {
      const ok = await confirm({
        title: 'Station entfernen?',
        message: (
          <>
            Berufsstation <strong>"{occ.title}"</strong> wirklich entfernen?
          </>
        ),
        confirmLabel: 'Entfernen',
        variant: 'danger',
      });
      if (!ok) return;
      removeOccupation.mutate(occ.id);
    },
    [confirm, removeOccupation],
  );

  return (
    <div>
      {/* Kopfzeile: Titel + Aktionen */}
      <div className="mb-2 flex items-center justify-between gap-2">
        <h4 className="flex items-center gap-1.5 text-xs font-medium text-slate-400">
          <Briefcase className="h-4 w-4" aria-hidden />
          Laufbahn
        </h4>
        <div className="flex items-center gap-1">
          {canViewArchive && (
            <button
              onClick={() => navigate(`/archive?bio=${person.id}`)}
              className="flex items-center gap-1 rounded px-1.5 py-1 text-xs font-medium text-blue-600
                         hover:bg-blue-50 hover:text-blue-800"
            >
              <ScrollText className="h-4 w-4" aria-hidden />
              {person.biography ? 'Lebenslauf ansehen' : 'Lebenslauf anlegen'}
            </button>
          )}
          {canManage && !editing && (
            <button
              onClick={() => setEditing('new')}
              className="flex items-center gap-1 rounded px-1.5 py-1 text-xs font-medium text-slate-500
                         hover:bg-slate-100 hover:text-slate-700"
            >
              <Plus className="h-4 w-4" aria-hidden />
              Station
            </button>
          )}
        </div>
      </div>

      {/* Inline-Formular (Anlegen oder Bearbeiten) */}
      {editing && (
        <div className="mb-3">
          <OccupationForm
            key={editing === 'new' ? 'new' : editing.id}
            initial={
              editing === 'new'
                ? EMPTY_FORM
                : {
                    title: editing.title,
                    fromText: editing.fromText ?? '',
                    toText: editing.toText ?? '',
                    employer: editing.employer ?? '',
                    place: editing.place ?? '',
                    notes: editing.notes ?? '',
                  }
            }
            isPending={saveOccupation.isPending}
            onSubmit={(input) =>
              saveOccupation.mutate({
                occupationId: editing === 'new' ? null : editing.id,
                input,
              })
            }
            onCancel={() => setEditing(null)}
          />
        </div>
      )}

      {/* Zeitleiste der Stationen */}
      {occupations.length > 0 && (
        <ol className="ml-1 space-y-3 border-l border-slate-200 pl-4">
          {occupations.map((occ) => {
            const period = formatPeriod(occ);
            const employerPlace = [occ.employer, occ.place].filter(Boolean).join(' · ');
            return (
              <li key={occ.id} className="group relative">
                <span
                  className="absolute -left-[21px] top-1.5 h-2 w-2 rounded-full bg-slate-300"
                  aria-hidden
                />
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm">
                      <span className="font-semibold text-slate-800">{occ.title}</span>
                      {period && <span className="ml-2 text-xs text-slate-400">{period}</span>}
                    </p>
                    {employerPlace && (
                      <p className="mt-0.5 text-xs text-slate-500">{employerPlace}</p>
                    )}
                    {occ.notes && (
                      <p className="mt-0.5 text-xs leading-relaxed text-slate-400">{occ.notes}</p>
                    )}
                  </div>
                  {canManage && (
                    // Auf Touch immer sichtbar; am Desktop per Hover ODER Tastaturfokus
                    // einblenden (WCAG 2.4.7 – Fokus darf nicht unsichtbar sein).
                    <div className="flex shrink-0 gap-0.5 transition-opacity sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
                      <button
                        onClick={() => setEditing(occ)}
                        className="rounded p-1 text-slate-300 hover:bg-blue-50 hover:text-blue-600"
                        aria-label="Station bearbeiten"
                      >
                        <Pencil className="h-4 w-4" aria-hidden />
                      </button>
                      <button
                        onClick={() => handleDelete(occ)}
                        className="rounded p-1 text-slate-300 hover:bg-red-50 hover:text-red-500"
                        aria-label="Station entfernen"
                      >
                        <Trash2 className="h-4 w-4" aria-hidden />
                      </button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {/* Fallback: altes Freitextfeld "Beruf" ohne erfasste Stationen */}
      {occupations.length === 0 && person.occupation && (
        <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-2.5">
          <p className="text-sm font-semibold text-slate-800">{person.occupation}</p>
          <p className="mt-0.5 text-xs text-slate-400">aus Beruf-Feld</p>
        </div>
      )}

      {/* Leerer Zustand */}
      {occupations.length === 0 && !person.occupation && !editing && (
        <p className="py-1 text-sm text-slate-400">Noch keine Berufsstationen erfasst.</p>
      )}
    </div>
  );
}
